import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

export const KEYS_PAGE_SIZE_OPTIONS = [10, 30, 50] as const;
export const KEYS_DEFAULT_PAGE_SIZE = 50;

type ParamUpdates = Record<string, string | number | null | undefined>;

/**
 * Paging and filters for the Keys tab's tables, kept in the URL (`page` is 1-based, `size`, plus
 * any filters) so the fob detail's "Keys" breadcrumb and the back button return to the same page.
 */
export function useKeysSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Kept across filter changes: it carries the fob detail's "back to Keys" link.
  const locationState: unknown = useLocation().state;

  const pageParam = Number.parseInt(searchParams.get("page") ?? "", 10);
  const page = Number.isFinite(pageParam) && pageParam > 1 ? pageParam - 1 : 0;
  const sizeParam = Number.parseInt(searchParams.get("size") ?? "", 10);
  const pageSize = (KEYS_PAGE_SIZE_OPTIONS as readonly number[]).includes(sizeParam)
    ? sizeParam
    : KEYS_DEFAULT_PAGE_SIZE;

  const update = useCallback(
    (updates: ParamUpdates) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [name, value] of Object.entries(updates)) {
            if (value === null || value === undefined || value === "") {
              next.delete(name);
            } else {
              next.set(name, String(value));
            }
          }
          return next;
        },
        { replace: true, state: locationState },
      );
    },
    [setSearchParams, locationState],
  );

  const setPage = useCallback(
    (nextPage: number) => update({ page: nextPage > 0 ? nextPage + 1 : null }),
    [update],
  );

  const setPageSize = useCallback(
    (size: number) =>
      update({ size: size === KEYS_DEFAULT_PAGE_SIZE ? null : size, page: null }),
    [update],
  );

  /** Sets filters and goes back to the first page. */
  const setFilters = useCallback(
    (filters: ParamUpdates) => update({ ...filters, page: null }),
    [update],
  );

  return {
    page,
    pageSize,
    getParam: (name: string) => searchParams.get(name) ?? "",
    setPage,
    setPageSize,
    setFilters,
  };
}

/**
 * A text filter that's typed into immediately but written to the URL (and so queried) after a
 * pause. Follows the URL when it changes some other way (back button, links).
 */
export function useDebouncedFilter(
  urlValue: string,
  onCommit: (value: string) => void,
  delay = 300,
) {
  const [value, setValue] = useState(urlValue);
  const committed = useRef(urlValue);
  const commit = useRef(onCommit);
  commit.current = onCommit;

  useEffect(() => {
    if (value === committed.current) return;
    const timer = setTimeout(() => {
      committed.current = value;
      commit.current(value);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  useEffect(() => {
    if (urlValue !== committed.current) {
      committed.current = urlValue;
      setValue(urlValue);
    }
  }, [urlValue]);

  return [value, setValue] as const;
}
